"use client";
import { useEffect, useRef, useState } from "react";
import ScoopsCard from "./cards/ScoopsCard";
import { getPostings } from "../../services/scoops";
function ScrollingColumn({ scoops, speed, direction = "up" }) {
  const columnRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const column = columnRef.current;
    const container = containerRef.current;
    if (!column || !container || scoops.length === 0) return;

    let animationId;
    let translateY = 0;

    const animate = () => {
      const columnHeight = column.scrollHeight;
      const containerHeight = container.offsetHeight;
      
      if (direction === "up") {
        translateY -= speed;
        if (translateY <= -columnHeight / 2) {
          translateY = 0;
        }
      } else {
        translateY += speed;
        if (translateY >= 0) {
          translateY = -columnHeight / 2;
        }
      }

      column.style.transform = `translateY(${translateY}px)`;
      animationId = requestAnimationFrame(animate);
    };

    const timeoutId = setTimeout(() => {
      animationId = requestAnimationFrame(animate);
    }, 100);

    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
      clearTimeout(timeoutId);
    };
  }, [speed, direction, scoops]);

  const duplicatedScoops = scoops.length > 0 ? [...scoops, ...scoops] : [];

  return (
    <div 
      ref={containerRef}
      style={{ 
        height: '100%',
        width: '100%',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      <div 
        ref={columnRef} 
        className="flex flex-col"
        style={{ 
          gap: 'clamp(12px, 1.04vw, 20px)',
          margin: 0,
          padding: 0,
          willChange: 'transform'
        }}
      >
        {duplicatedScoops.map((scoop, index) => (
          <div 
            key={`${scoop.postingid}-${index}`} 
            className="flex-shrink-0"
            style={{ width: '100%' }}
          >
            <ScoopsCard
              post={scoop}
              images={scoop.images}
              isMobile={false}
              className="w-full"
            />
          </div>
        ))}
      </div>
    </div>
  );
}

export default function ScoopsSection() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        setLoading(true);
        const geohash = localStorage.getItem("geohash") || "9v6m";
        const response = await getPostings({ geohash, offset: 0 });
        const transformedPosts = response.posts.map((post) => {
          let firstImage = null;
          if (post.photopath) {
            if (typeof post.photopath === "string") {
              const imageArray = post.photopath.split(",").map(img => img.trim()).filter(img => img);
              firstImage = imageArray.length > 0 ? imageArray[0] : null;
            } else if (Array.isArray(post.photopath)) {
              firstImage = post.photopath.length > 0 ? post.photopath[0] : null;
            } else {
              firstImage = post.photopath;
            }
          }
          
          return {
            postingid: post.postingid,
            username: post.username,
            avatar_url: post.avatar_url,
            createdate: post.createdate,
            scoopname: post.scoopname,
            contentinfo: post.contentinfo,
            totalLikes: post.totalLikes,
            commentCount: post.comments?.length || 0,
            hoops: post.hoops || [],
            likes: post.likes || [],
            comments: post.comments || [],
            images: firstImage ? [firstImage] : [],
          };
        });

        setPosts(transformedPosts);
        setError(null);
      } catch (err) {
        setError("Failed to load posts");
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();

    const handleGeohashChange = () => {
      fetchPosts();
    };

    window.addEventListener("geohashChanged", handleGeohashChange);
    window.addEventListener("storage", (e) => {
      if (e.key === "geohash") {
        handleGeohashChange();
      }
    });

    return () => {
      window.removeEventListener("geohashChanged", handleGeohashChange);
      window.removeEventListener("storage", handleGeohashChange);
    };
  }, []);

  const column1Data = posts.filter((_, index) => index % 3 === 0);
  const column2Data = posts.filter((_, index) => index % 3 === 1);
  const column3Data = posts.filter((_, index) => index % 3 === 2);

  if (loading) {
    return (
      <div className="py-16 bg-gradient-to-r from-orange-50 to-[#7B189F]/10">
        <div className="container mx-auto px-6">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#7B189F] mb-4"></div>
              <p className="text-[#7B189F] font-medium">
                Loading community posts...
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-16 bg-gradient-to-r from-orange-50 to-[#7B189F]/10">
        <div className="container mx-auto px-6">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <p className="text-red-600 font-medium">{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="mt-4 px-6 py-2 bg-[#7B189F] text-white rounded-full hover:bg-[#6a1489]"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (posts.length === 0) {
    return (
      <div className="py-16 bg-gradient-to-r from-orange-50 to-[#7B189F]/10">
        <div className="container mx-auto px-6">
          <div className="flex items-center justify-center min-h-[400px]">
            <p className="text-gray-600 font-medium">No posts available yet.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-8 sm:pt-12 md:pt-0" style={{ 
      paddingBottom: 0,
      paddingLeft: 0,
      paddingRight: 0,
      backgroundColor: '#EFE3CE'
    }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:wght@700&family=Afacad:wght@400;500;600&family=Inter:wght@700&display=swap');
        
        .fraunces-bold {
          font-family: 'Fraunces', serif;
          font-weight: 700;
        }
        
        .afacad-font {
          font-family: 'Afacad', sans-serif;
        }

        .horizontal-scroll {
          scrollbar-width: none;
          -ms-overflow-style: none;
        }
        
        .horizontal-scroll::-webkit-scrollbar {
          display: none;
        }
      `}</style>

      <div className="mx-auto" style={{ maxWidth: '1920px', margin: '0 auto', position: 'relative' }}>
        <div 
          className="hidden lg:block"
          style={{
            position: 'absolute',
            top: '-2px',
            left: 0,
            right: 0,
            height: '2px',
            borderTop: '2px dashed #3B82F6',
            zIndex: 10
          }}
        />
        
        <div 
          className="hidden lg:grid lg:grid-cols-12" 
          style={{ 
            alignItems: 'stretch', 
            height: '800px',
            gap: 0,
            overflow: 'hidden',
            marginTop: '2px'
          }}
        >
          <div 
            className="lg:col-span-4 xl:col-span-4 2xl:col-span-4 flex flex-col justify-between"
            style={{ 
              height: '100%',
              overflow: 'hidden',
              backgroundColor: '#EFE3CE',
              paddingTop: 'clamp(40px, 3.47vw, 67px)',
              paddingBottom: 0,
              paddingLeft: 'clamp(30px, 2.6vw, 50px)',
              paddingRight: 'clamp(30px, 2.6vw, 50px)',
              position: 'relative'
            }}
          >
            <div style={{ flexShrink: 0, marginBottom: 'clamp(30px, 2.6vw, 50px)' }}>
              <h2
                className="fraunces-bold leading-tight"
                style={{ 
                  color: "#7B189F",
                  fontSize: 'clamp(32px, 2.83vw, 56px)',
                  fontWeight: 700,
                  lineHeight: '1.2',
                  marginBottom: 'clamp(20px, 1.74vw, 34px)',
                  marginTop: 0,
                  padding: 0
                }}
              >
                LISTEN WHAT
                <br />
                OUR COMMUNITY
                <br />
                SAYS..
              </h2>
              <button
                className="text-white rounded-full hover:opacity-90 transition-opacity inline-flex items-center justify-center"
                style={{ 
                  backgroundColor: "#7B189F",
                  width: 'clamp(100px, 7.02vw, 139px)',
                  height: 'clamp(25px, 1.77vw, 35px)',
                  fontSize: 'clamp(12px, 0.83vw, 16px)',
                  fontWeight: 700,
                  fontFamily: 'Inter, sans-serif',
                  borderRadius: '17.5px',
                  padding: 0
                }}
              >
                Join Now
              </button>
            </div>

            <div 
              className="relative"
              style={{ 
                marginTop: 'auto',
                paddingBottom: 0,
                flexShrink: 0,
                alignSelf: 'flex-end',
                width: '100%',
                display: 'flex',
                alignItems: 'flex-end'
              }}
            >
              <img
                src="/assets/community-illustration.png"
                alt="Community member with reviews"
                style={{ 
                  width: '100%',
                  height: 'auto',
                  objectFit: 'contain',
                  display: 'block',
                  maxHeight: 'none',
                  objectPosition: 'bottom'
                }}
              />
            </div>
          </div>

          <div 
            className="lg:col-span-8 xl:col-span-8 2xl:col-span-8 grid grid-cols-3" 
            style={{ 
              height: '100%',
              gap: 'clamp(12px, 1.04vw, 20px)',
              overflow: 'hidden',
              margin: 0,
              paddingTop: 0,
              paddingBottom: 0,
              paddingLeft: 'clamp(20px, 1.74vw, 34px)',
              paddingRight: 'clamp(20px, 1.74vw, 34px)',
              backgroundColor: '#EFE3CE'
            }}
          >
            {column1Data.length > 0 && (
              <div style={{ 
                height: '100%', 
                width: '100%',
                margin: 0,
                padding: 0,
                overflow: 'hidden'
              }}>
                <ScrollingColumn
                  scoops={column1Data}
                  speed={0.5}
                  direction="up"
                />
              </div>
            )}
            {column2Data.length > 0 && (
              <div style={{ 
                height: '100%', 
                width: '100%',
                margin: 0,
                padding: 0,
                overflow: 'hidden'
              }}>
                <ScrollingColumn
                  scoops={column2Data}
                  speed={0.7}
                  direction="down"
                />
              </div>
            )}
            {column3Data.length > 0 && (
              <div style={{ 
                height: '100%', 
                width: '100%',
                margin: 0,
                padding: 0,
                overflow: 'hidden'
              }}>
                <ScrollingColumn
                  scoops={column3Data}
                  speed={0.6}
                  direction="up"
                />
              </div>
            )}
          </div>
        </div>

        <div className="lg:hidden">
          <h2
            className="fraunces-bold text-2xl sm:text-3xl md:text-4xl mb-3 sm:mb-5 md:mb-6"
            style={{ color: "#7B189F" }}
          >
            Listen What Our Community Says..
          </h2>

          <div className="horizontal-scroll overflow-x-auto">
            <div className="flex gap-3 sm:gap-4 md:gap-5 pb-4">
              {posts.map((scoop) => (
                <div key={scoop.postingid} className="flex-shrink-0 w-72 sm:w-80 md:w-96">
                  <ScoopsCard
                    post={scoop}
                    images={scoop.images}
                    isMobile={true}
                    className="w-full"
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
